import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchExtPartnerCheckIns } from '../services/api';
import { normalizeStatus } from '../utils/webcheckinStatus';

// 'all' or a status code, as the status cards use.
const normalizeStatusFilter = (status) => {
  const normalized = normalizeStatus(status);
  return !normalized || normalized === 'ALL' ? 'all' : normalized;
};

/**
 * Custom hook for managing external partner check-ins
 * @returns {Object} Object containing check-in data and methods
 */
const useExtPartnerCheckIns = () => {
  const [checkIns, setCheckIns] = useState([]);
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    itemsPerPage: 50,
    pageNumber: 0,
    totalItems: 0,
    totalPages: 0
  });

  // Filter states - Set default departureDateMin to today's date
  const [filters, setFilters] = useState(() => {
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    return {
      pnr: '',
      partnerName: '',
      airlineName: '',
      subscriptionId: '',
      flightNumber: '',
      searchField: 'pnr',
      searchValue: '',
      checkinStatus: '',
      departureDateMin: today,
      departureDateMax: ''
    };
  });

  // Keep a ref of filters to avoid recreating callbacks
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Track if initial load has been done
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [lastLoadParams, setLastLoadParams] = useState(null);

  // Load check-ins with pagination
  const loadCheckIns = useCallback(async (
    page = 0,
    itemsPerPage = 50,
    customFilters = {},
    options = {}
  ) => {
    setLoading(true);
    setError(null);

    try {
      const mergedFilters = {
        page: page,
        limit: itemsPerPage,
        ...filtersRef.current,
        ...customFilters
      };

      const params = { ...mergedFilters };
      if (params.searchValue) {
        const field = params.searchField || 'pnr';
        params[field] = params.searchValue;
      }
      delete params.searchField;
      delete params.searchValue;

      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      // Check if we're making the same request as before
      const currentParams = JSON.stringify(params);
      if (!options.force && hasInitiallyLoaded && lastLoadParams === currentParams) {
        console.log('Skipping duplicate API call for external partner check-ins');
        setLoading(false);
        return;
      }

      console.log('Loading external partner check-ins with params:', params);

      const response = await fetchExtPartnerCheckIns(params);
      setCheckIns(response.data);

      // Update pagination info from API response
      setPagination(prev => ({
        ...prev,
        pageNumber: page,
        itemsPerPage: itemsPerPage,
        totalItems: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));

      // Mark as initially loaded
      setHasInitiallyLoaded(true);
      setLastLoadParams(currentParams);
    } catch (err) {
      setError(err.message || 'Failed to load external partner check-ins');
      console.error('Error loading external partner check-ins:', err);
    } finally {
      setLoading(false);
    }
  }, [hasInitiallyLoaded, lastLoadParams]);

  // Change page
  const changePage = useCallback((newPage) => {
    loadCheckIns(newPage, pagination.itemsPerPage);
  }, [loadCheckIns, pagination.itemsPerPage]);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  // Search with filters
  const searchWithFilters = useCallback(() => {
    setHasInitiallyLoaded(false);
    setStatusFilter(normalizeStatusFilter(filtersRef.current.checkinStatus));
    loadCheckIns(0, pagination.itemsPerPage, {}, { force: true });
  }, [loadCheckIns, pagination.itemsPerPage]);

  // Clear filters
  const clearFilters = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
      pnr: '',
      partnerName: '',
      airlineName: '',
      subscriptionId: '',
      flightNumber: '',
      searchField: 'pnr',
      searchValue: '',
      checkinStatus: '',
      departureDateMin: today,
      departureDateMax: ''
    });
    setStatusFilter('all');
    setHasInitiallyLoaded(false);
  }, []);

  // Filter check-ins by status
  const filteredCheckIns = statusFilter === 'all'
    ? checkIns
    : checkIns.filter(ci => normalizeStatus(ci.status) === statusFilter);

  return {
    checkIns: filteredCheckIns,
    allCheckIns: checkIns,
    selectedCheckIn,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    pagination,
    changePage,
    loadCheckIns,
    setSelectedCheckIn,
    filters,
    updateFilters,
    searchWithFilters,
    clearFilters
  };
};

export default useExtPartnerCheckIns;
